import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { matchPath } from 'react-router-dom';
import { Whisper, Tooltip } from 'rsuite';
import { ChevronLeft, ChevronRight, LogOut } from 'lucide-react';
import MenuItems from '../../config/MenuItems';
import { useClientDetail } from '../../pages/clients/hooks/useClients';

import { tenantConfig } from '../../config/tenant';
import { MODULE, MODULE_PERMISSIONS } from '../../shared/constants/permissions';
import { useAuth } from '../../contexts/AuthContext';
import { ConfirmModal } from '../../shared/molecules/modal';
import mgsLogo2 from '../../assets/images/mgs-ico.png';
import NavItem from './NavItem';
import styles from './Sidebar.module.scss';
import { MODAL_TITLES, MODAL_STRINGS, BUTTON_LABELS, ARIA_LABELS } from '../../shared/constants/strings';

/** The entry that becomes the client's own name for an account belonging to one client. */
const CLIENTS_MENU_ID = 'clients';

interface SidebarProps {
  expanded?: boolean;
  onToggle?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ expanded: expandedProp, onToggle }) => {
  const location = useLocation();
  const { hasAnyPermission, ownClientId, logout, isModuleEnabled } = useAuth();

  const namedClientId = hasAnyPermission(MODULE_PERMISSIONS[MODULE.CLIENT]) ? ownClientId : undefined;
  const { data: ownClient } = useClientDetail(namedClientId);
  const [activeKey, setActiveKey] = useState('');
  const [localExpanded, setLocalExpanded] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  const expanded = expandedProp ?? localExpanded;
  const handleToggle = onToggle ?? (() => setLocalExpanded((prev) => !prev));

  const visibleItems = useMemo(() => {
    const passesPermission = (item: (typeof MenuItems)[number]) =>
      !item.requiredPermissions || item.requiredPermissions.length === 0 || hasAnyPermission(item.requiredPermissions);

    const passesModule = (item: (typeof MenuItems)[number]) =>
      !item.requiredModule || isModuleEnabled(item.requiredModule);

    return MenuItems.filter((i) => passesPermission(i) && passesModule(i)).map((item) =>
      item.id === CLIENTS_MENU_ID && namedClientId
        ? // The name arrives with the client, so the label falls back until it does.
          { ...item, name: ownClient?.name ?? item.name, link: `/clients/${namedClientId}` }
        : item,
    );
  }, [hasAnyPermission, isModuleEnabled, namedClientId, ownClient]);

  const mainItems = useMemo(() => visibleItems.filter((i) => i.section === 'main'), [visibleItems]);
  const bottomItems = useMemo(() => visibleItems.filter((i) => i.section === 'bottom'), [visibleItems]);

  // Active menu detection — longest match wins for specificity
  useEffect(() => {
    const currentPath = location.pathname;

    const sorted = [...MenuItems].sort((a, b) => (b.link?.length ?? 0) - (a.link?.length ?? 0));

    for (const item of sorted) {
      if (item.link && matchPath({ path: item.link, end: false }, currentPath)) {
        setActiveKey(item.id);
        return;
      }
      if (item.matchPaths?.some((p) => matchPath({ path: p, end: false }, currentPath))) {
        setActiveKey(item.id);
        return;
      }
    }
  }, [location.pathname]);

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleSignOutClick = () => {
    setShowLogoutConfirm(true);
  };

  const handleSignOutConfirm = async () => {
    setShowLogoutConfirm(false);
    await logout();
  };

  const signOutButton = (
    <button
      className={styles.signOutBtn}
      onClick={handleSignOutClick}
      // Collapsed, the icon is the only content — name the button for screen readers.
      aria-label={expanded ? undefined : MODAL_TITLES.SIGN_OUT}
      type="button"
    >
      <LogOut size={20} />
      {expanded && <span>{MODAL_TITLES.SIGN_OUT}</span>}
    </button>
  );

  return (
    <>
      {/* Mobile toggle */}
      <button
        className={styles.mobileToggle}
        onClick={() => setMobileOpen((prev) => !prev)}
        aria-label={ARIA_LABELS.TOGGLE_SIDEBAR}
        type="button"
      >
        {mobileOpen ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
      </button>

      {/* Sidebar */}
      <aside
        className={`${styles.sidebar} ${expanded ? '' : styles.collapsed} ${mobileOpen ? styles.mobileOpen : ''}`}
        data-testid={ARIA_LABELS.SIDEBAR}
      >
        {/* Header — sticky */}
        <div className={styles.sidebarHeader}>
          <div className={styles.logoArea}>
            <img src={mgsLogo2} alt={tenantConfig.branding.name} className={styles.logoImg} />
            <div className={styles.logoText}>
              <span className={styles.logoTextItem}>Multi</span>
              <span className={styles.logoTextItem}>Genesys</span>
            </div>
          </div>
          {/* Toggle button — vertically centered on sidebar edge */}
          <button
            className={styles.toggleBtn}
            onClick={handleToggle}
            aria-label={expanded ? ARIA_LABELS.COLLAPSE_SIDEBAR : ARIA_LABELS.EXPAND_SIDEBAR}
            type="button"
          >
            {expanded ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          </button>
        </div>

        {/* Scrollable nav body */}
        <nav className={styles.navBody}>
          <ul className={styles.navList}>
            {mainItems.map((item) => (
              <NavItem
                key={item.id}
                id={item.id}
                link={item.link}
                name={item.name}
                icon={item.icon}
                active={activeKey === item.id}
                expanded={expanded}
              />
            ))}
          </ul>
        </nav>

        {/* Bottom section — pinned */}
        <div className={styles.bottomSection}>
          <div className={styles.divider} />
          <ul className={styles.navList}>
            {bottomItems.map((item) => (
              <NavItem
                key={item.id}
                id={item.id}
                link={item.link}
                name={item.name}
                icon={item.icon}
                active={activeKey === item.id}
                expanded={expanded}
              />
            ))}
          </ul>
          <div className={styles.divider} />
          {expanded ? (
            signOutButton
          ) : (
            <Whisper trigger="hover" placement="right" speaker={<Tooltip>{MODAL_TITLES.SIGN_OUT}</Tooltip>}>
              {signOutButton}
            </Whisper>
          )}
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && <div className={styles.overlay} onClick={() => setMobileOpen(false)} />}

      <ConfirmModal
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={handleSignOutConfirm}
        title={MODAL_TITLES.SIGN_OUT}
        message={MODAL_STRINGS.SIGN_OUT_CONFIRMATION}
        confirmLabel={MODAL_TITLES.SIGN_OUT}
        cancelLabel={BUTTON_LABELS.CANCEL}
        variant={MODAL_STRINGS.DANGER}
      />
    </>
  );
};

export default Sidebar;
